import { useTypedLocalStorage } from '@fox/atoms';
import React, { createContext, PropsWithChildren, ReactNode, useContext, useEffect, useState } from 'react';
import { GraphColumn, QueryFilter, SortState } from '.';

type ColumnTitleContextValue = {
	dataIndex: string;
};

type WidthState = Record<string, number>;
type OrderState = string[];

type FilterParamContextValue = Record<string, unknown>;

type PivotContextValue = {
	groupBy: string;
	groupByValue: string;
};

const FilterParamContext = createContext<FilterParamContextValue | undefined>(undefined);

export const FilterParamContextProvider = FilterParamContext.Provider;

export const useFilterParamContext = () => useContext(FilterParamContext);

export const ColumnTitleContext = createContext<ColumnTitleContextValue | undefined>(undefined);

const PivotContext = createContext<PivotContextValue | undefined>(undefined);

export const PivotContextProvider = PivotContext.Provider;

export const usePivotContext = () => useContext(PivotContext);

type AutoDataGridContextValue = {
	id: string;
	sort?: SortState;
	setSortColumn: (sort: SortState | null) => void;
	pinnedColumnsDataIndex: string[];
	setPinnedColumnsDataIndex: (pinnedColumnsDataIndex: string[]) => void;
	filters: QueryFilter[];
	setFilters: (filters: QueryFilter[]) => void;
	filterParams: Record<string, unknown>;
	addColumnFilterParams: (dataIndex: string, filterType: string) => Promise<string>;
	loading: boolean;
	hiddenColumnsDataIndex: string[];
	setHiddenColumnsDataIndex: (hiddenColumnsDataIndex: string[]) => void;
	allColumns: GraphColumn<unknown>[];
	groupedColumns: Record<string, { grouped: boolean; filterType?: string; title: ReactNode }>;
	widths: WidthState;
	setWidths: React.Dispatch<WidthState>;
	order: OrderState;
	setOrder: (order: string[]) => void;
	search: string;
	setSearch: (search: string) => void;
	groupBy: string;
	setGroupBy: (groupBy: string) => void;
	colVis?: string;
	setColVis?: (colVis: string) => void;
	resetLocalStorage?: () => void;
	localSaveOnly?: boolean;
};

const AutoDataGridContext = createContext<AutoDataGridContextValue | undefined>(undefined);

export const selectableColumn = (col: GraphColumn<unknown>) => col.title && (typeof col.hideable !== 'boolean' || col.hideable);

function colVis2Record(colVis: string, anyDefault: boolean, columns?: GraphColumn<unknown>[]) {
	return colVis
		? JSON.parse(colVis)
		: columns?.reduce((prev, cur) => ({ ...prev, [cur.dataIndex]: !anyDefault ? true : cur.defaultColumn }), {});
}

// TODO: This context needs to be broken up so that the filter/groupby/hiding/pinning aspects are their
// own separate react contexts. This would then allow us to kill off a whole bunch of props here that
// are primarily there to allow this state to be "lifted up"

export type GridGroupable = {
	sort?: SortState;
	/** Advise the consumer of this component that the sort order has requested to be updated. This is only required if you specify at least one column as sortable. */
	onSort?: (SortState: SortState) => void;
};

export type GridSearchable = {
	search?: string;
	setSearch?: (search: string) => void;
};

export type GridSortable = {
	groupBy?: string;
	setGroupBy?: (groupBy: string) => void;
};

export type GridColVisible = {
	colVis?: string;
	setColVis?: (colVis: string) => void;
};

export type GridColPinnable = {
	pinnedColumnsDataIndex?: string[];
	setPinnedColumnsDataIndex?: (pinnedColumnsDataIndex: string[]) => void;
};

export type GridOrder = {
	colOrder?: string[];
	setColOrder?: (pinnedColumnsDataIndex: string[]) => void;
};

export type GridFilterable = {
	filters?: readonly QueryFilter[];
	setFilters?: (filters: QueryFilter[]) => void;
};

type AutoDataGridContextProviderProps = {
	filterParams?: Record<string, unknown>;
	addColumnFilterParams?: (dataIndex: string, filterType: string) => Promise<string>;
	loading: boolean;
	id: string;
	columns: GraphColumn<unknown>[];
	localSaveOnly?: boolean;
} & GridFilterable &
	GridGroupable &
	GridSortable &
	GridColPinnable &
	GridColVisible &
	GridSearchable &
	GridOrder;

export type LocalStorageViewData = {
	widths: Record<string, number>;
	order: string[];
	sort: SortState;
	pinned: string[];
	vis: string;
	filters: QueryFilter[];
};

export function buildInitialViewData(defaultSort?: SortState): LocalStorageViewData {
	return {
		widths: {},
		order: [],
		sort: defaultSort ?? undefined,
		pinned: [],
		vis: '',
		filters: []
	};
}

export const AutoDataGridContextProvider: React.FC<PropsWithChildren<AutoDataGridContextProviderProps>> = props => {
	const [localViewData, setLocalViewData] = useTypedLocalStorage<LocalStorageViewData>(
		`TW_${props.id}_view`,
		buildInitialViewData(props?.sort)
	);

	const colOrder = props.localSaveOnly ? localViewData?.order : props?.colOrder;
	const pinnedColumns = props.localSaveOnly ? localViewData?.pinned : props?.pinnedColumnsDataIndex;
	const visibleColumns = props.localSaveOnly ? localViewData?.vis : props?.colVis;
	const colSort = props.localSaveOnly ? localViewData?.sort : props.sort;
	const colFilters = props.localSaveOnly ? localViewData?.filters : props.filters;
	const anyDefault = props.columns?.some(f => f.defaultColumn === true);

	const [widths, setWidths] = useState<Record<string, number>>(localViewData.widths ?? undefined);
	const setColWidths = (values: Record<string, number>, reset?: boolean) => {
		if (!reset) setLocalViewData({ ...localViewData, widths: values });
		setWidths(values);
	};

	const [colVis, onSetColVis] = useState<Record<string, boolean>>(colVis2Record(visibleColumns, anyDefault, props.columns));
	useEffect(() => {
		onSetColVis(colVis2Record(visibleColumns, anyDefault, props.columns));
	}, [props.colVis, anyDefault, props.columns]);
	const setColVis = React.useCallback(
		(value: string, reset?: boolean) => {
			if (props.localSaveOnly && !reset) {
				setLocalViewData({ ...localViewData, vis: JSON.stringify(colVis2Record(value, anyDefault, props.columns)) });
			} else {
				props.setColVis?.(value);
			}
			onSetColVis(colVis2Record(value, anyDefault, props.columns));
		},
		[anyDefault, props.columns]
	);
	const columnKey = props.columns?.map(c => c.dataIndex).join(', ');
	React.useEffect(() => {
		const cols = props.columns;
		const prev = JSON.parse(JSON.stringify(colVis));
		cols.forEach(c => {
			if (prev[c.dataIndex] === undefined) {
				prev[c.dataIndex] = !anyDefault ? true : c.defaultColumn;
			}
		});
		onSetColVis(prev);
	}, [columnKey, anyDefault]);

	const [pinnedColumnsDataIndex, onSetPinnedColumnsDataIndex] = useState<string[]>(pinnedColumns ?? []);
	useEffect(() => {
		const np = pinnedColumns ?? [];
		onSetPinnedColumnsDataIndex(np);
	}, [props.pinnedColumnsDataIndex, localViewData.pinned]);
	const setPinnedColumnsDataIndex = (value: string[], reset?: boolean) => {
		if (props.localSaveOnly && !reset) {
			setLocalViewData({ ...localViewData, pinned: value });
		}
		props.setPinnedColumnsDataIndex?.(value);
		onSetPinnedColumnsDataIndex(value);
	};

	const [columnOrder, onSetColumnOrder] = useState<string[]>(colOrder ?? []);

	useEffect(() => {
		const np = colOrder ?? [];

		onSetColumnOrder(np);
	}, [props.pinnedColumnsDataIndex, localViewData.pinned]);

	const setColumnOrder = (value: string[], reset?: boolean) => {
		if (props.localSaveOnly && !reset) {
			setLocalViewData({ ...localViewData, order: value });
		} else {
			props.setColOrder?.(value);
		}
		onSetColumnOrder(value);
	};
	const [sort, onSortState] = useState<SortState>(colSort ?? undefined);
	useEffect(() => {
		const ns = colSort ?? undefined;
		onSortState(ns);
	}, [props.sort, localViewData.sort]);
	const onSort = (sort: SortState, reset?: boolean) => {
		if (props.localSaveOnly && !reset) {
			setLocalViewData({ ...localViewData, sort: sort });
		}
		props.onSort?.(sort);
		onSortState(sort);
	};

	const [filters, setFilterState] = useState<QueryFilter[]>(Array.isArray(colFilters) ? [...colFilters] : []);
	useEffect(() => {
		if (!props.localSaveOnly) setFilterState(Array.isArray(colFilters) ? [...colFilters] : []);
	}, [props.filters]);

	const setFilters = (filters: QueryFilter[], reset?: boolean) => {
		if (props.localSaveOnly && !reset) {
			setLocalViewData({ ...localViewData, filters: filters });
		}
		props.setFilters?.(filters);
		setFilterState(filters);
	};

	const [groupBy, onSetGroupBy] = useState<string>(props.groupBy ?? '');
	useEffect(() => {
		const ng = props.groupBy ?? '';
		//console.log("New group by", ng);
		onSetGroupBy(ng);
	}, [props.groupBy]);

	const setGroupBy = (value: string) => {
		props.setGroupBy?.(value);
		onSetGroupBy(value);
	};

	const hiddenColumnsDataIndex = props.columns?.filter(c => colVis[c.dataIndex] === false).map(c => c.dataIndex);
	const setHiddenColumnsDataIndex = (hiddenColumnDataIndex: string[], currentView?: LocalStorageViewData) => {
		const current = currentView ?? localViewData;
		const newstate = props.columns
			?.map(c => c.dataIndex)
			.reduce((prev, cur) => {
				prev[cur] = hiddenColumnDataIndex.some(s => s === cur) ? false : true;
				return prev;
			}, {});
		// Can't quite call setColVis() directly, so just repeat the key bits here
		if (props.localSaveOnly) {
			setLocalViewData({ ...current, vis: JSON.stringify(newstate) });
		} else {
			props.setColVis?.(JSON.stringify(newstate));
		}
		onSetColVis(newstate);
	};

	const groupedColumns = props.columns?.reduce((prev, col) => {
		if (col.groupTitle) {
			if (!prev[col.groupTitle]) prev[col.groupTitle] = { group: true, title: col.groupTitle };
		} else if (selectableColumn(col)) {
			// skip over non-titled columns
			prev[col.dataIndex] = { group: false, title: col.title, filterType: col.filterType };
		}
		return prev;
	}, {});

	const resetLocalStorage = () => {
		const defaultView = buildInitialViewData(props?.sort);
		setLocalViewData(buildInitialViewData(props?.sort));
		localStorage.removeItem(`TW_${props.id}_view`);
		setColWidths(defaultView.widths, true);
		setColumnOrder(defaultView.order, true);
		onSort(defaultView.sort, true);
		setPinnedColumnsDataIndex(defaultView.pinned, true);
		setColVis(defaultView.vis, true);
		setFilters(defaultView.filters, true);
	};

	return (
		<AutoDataGridContext.Provider
			value={{
				id: props.id,
				filters: filters,
				setFilters: setFilters,
				pinnedColumnsDataIndex,
				setPinnedColumnsDataIndex,
				sort: sort,
				setSortColumn: onSort,
				filterParams: props.filterParams,
				addColumnFilterParams: props.addColumnFilterParams,
				loading: props.loading,
				allColumns: props.columns,
				groupedColumns,
				hiddenColumnsDataIndex,
				setHiddenColumnsDataIndex,
				widths: widths,
				setWidths: setColWidths,
				order: columnOrder,
				setOrder: setColumnOrder,
				search: props.search,
				setSearch: props.setSearch,
				groupBy,
				setGroupBy,
				colVis: colVis ? JSON.stringify(colVis) : undefined,
				setColVis,
				resetLocalStorage,
				localSaveOnly: props.localSaveOnly
			}}
		>
			{props.children}
		</AutoDataGridContext.Provider>
	);
};

export const useAutoDataGridContext = () => useContext(AutoDataGridContext);
